import type { Token } from "@/types";
import type { JupiterQuote } from "@/types";
import { TokenAvatar } from "./TokenAvatar";
import { ArrowDownIcon, ChevronIcon, RefreshIcon } from "./Icons";
import { BalanceSkeleton, QuoteRouteSkeleton } from "./Skeleton";
import { GaslessFeeDisplay } from "./GaslessFeeDisplay";
import { fmtNumber, fmtUsd, DEFAULT_SLIPPAGE_BPS } from "@/lib/utils";
import { calcMinReceived, formatPriceImpact } from "@/lib/jupiter";

interface TokenPanelProps {
	label: string;
	token: Token | undefined;
	amount: string;
	readOnly?: boolean;
	showPct?: boolean;
	isBalanceLoading?: boolean;
	walletConnected?: boolean;
	onAmountChange?: (v: string) => void;
	onPick: () => void;
	onPct?: (pct: number) => void;
}

function TokenPanel({
	label,
	token,
	amount,
	readOnly,
	showPct,
	isBalanceLoading,
	walletConnected,
	onAmountChange,
	onPick,
	onPct,
}: TokenPanelProps) {
	const num = parseFloat(amount) || 0;
	const usd = token ? num * token.usd : 0;

	return (
		<div className="token-panel">
			<div className="token-panel-head">
				<span className="token-panel-label">{label}</span>
			</div>
			<div className="token-panel-row">
				<input
					className="amount-input"
					inputMode="decimal"
					placeholder="0"
					value={amount}
					readOnly={readOnly}
					onChange={
						onAmountChange
							? (e) => onAmountChange(e.target.value.replace(/[^0-9.]/g, ""))
							: undefined
					}
				/>
				<button className="token-pick" onClick={onPick}>
					{token ? (
						<>
							<TokenAvatar token={token} size={28} />
							<span className="token-meta">
								<span className="token-sym">{token.symbol}</span>
								<span className="token-net">Solana</span>
							</span>
						</>
					) : (
						<span className="token-sym" style={{ padding: "0 4px" }}>
							Select
						</span>
					)}
					<ChevronIcon style={{ color: "var(--text-muted)" }} />
				</button>
			</div>
			<div className="token-panel-foot">
				<span>{fmtUsd(usd)}</span>
				<span className="balance-info">
					{walletConnected && (
						<span>
							Balance:{" "}
							{isBalanceLoading ? (
								<BalanceSkeleton />
							) : token ? (
								fmtNumber(token.balance, 4)
							) : (
								"—"
							)}
						</span>
					)}
					{showPct && onPct && token && walletConnected && (
						<>
							<button className="pct-btn" onClick={() => onPct(20)}>
								20%
							</button>
							<button className="pct-btn" onClick={() => onPct(50)}>
								50%
							</button>
							<button className="pct-btn" onClick={() => onPct(100)}>
								MAX
							</button>
						</>
					)}
				</span>
			</div>
		</div>
	);
}

interface SwapPanelProps {
	fromToken: Token | undefined;
	toToken: Token | undefined;
	fromAmount: string;
	quote: JupiterQuote | undefined;
	isQuoteLoading: boolean;
	isFeeLoading: boolean;
	feeSol: number | undefined;
	isBalanceLoading: boolean;
	walletConnected: boolean;
	onFromAmountChange: (v: string) => void;
	onPickFrom: () => void;
	onPickTo: () => void;
	onFlip: () => void;
	onRefreshQuote: () => void;
	onSubmit: () => void;
}

export function SwapPanel({
	fromToken,
	toToken,
	fromAmount,
	quote,
	isQuoteLoading,
	isFeeLoading,
	feeSol,
	isBalanceLoading,
	walletConnected,
	onFromAmountChange,
	onPickFrom,
	onPickTo,
	onFlip,
	onRefreshQuote,
	onSubmit,
}: SwapPanelProps) {
	const fromAmtNum = parseFloat(fromAmount) || 0;

	const toAmount = quote
		? fmtNumber(Number(quote.outAmount) / 10 ** (toToken?.decimals ?? 6), 6)
		: "";

	const priceImpact = quote ? parseFloat(quote.priceImpactPct) : 0;
	const highImpact = priceImpact > 1;

	const minReceived =
		quote && toToken
			? calcMinReceived(quote.outAmount, DEFAULT_SLIPPAGE_BPS, toToken.decimals)
			: null;

	const hasInsufficientBalance =
		fromToken && fromAmtNum > 0 && fromAmtNum > fromToken.balance;

	const isDisabled =
		!walletConnected ||
		!fromToken ||
		!toToken ||
		fromAmtNum <= 0 ||
		!quote ||
		Boolean(hasInsufficientBalance);

	const btnLabel = () => {
		if (!walletConnected) return "Connect wallet to swap";
		if (!fromToken || !toToken) return "Select tokens";
		if (fromAmtNum <= 0) return "Enter an amount";
		if (fromToken.address === toToken.address) return "Select different tokens";
		if (hasInsufficientBalance) return "Insufficient balance";
		if (isQuoteLoading) return "Fetching route…";
		if (!quote) return "No route found";
		return "Swap";
	};

	const setPct = (pct: number) => {
		if (!fromToken) return;
		onFromAmountChange(String((fromToken.balance * pct) / 100));
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
			<TokenPanel
				label="Sell"
				token={fromToken}
				amount={fromAmount}
				showPct
				isBalanceLoading={isBalanceLoading}
				walletConnected={walletConnected}
				onAmountChange={onFromAmountChange}
				onPick={onPickFrom}
				onPct={setPct}
			/>

			<div className="swap-arrow-wrap">
				<button className="swap-arrow" aria-label="Flip tokens" onClick={onFlip}>
					<ArrowDownIcon />
				</button>
			</div>

			<TokenPanel
				label="Buy"
				token={toToken}
				amount={isQuoteLoading ? "…" : toAmount}
				readOnly
				isBalanceLoading={isBalanceLoading}
				walletConnected={walletConnected}
				onPick={onPickTo}
			/>

			{isQuoteLoading && (
				<div style={{ marginTop: 12 }}>
					<QuoteRouteSkeleton />
				</div>
			)}

			{quote && !isQuoteLoading && (
				<div className="route-details">
					<div className="route-row">
						<span className="label">Price impact</span>
						<span className={highImpact ? "value danger" : "value"}>
							{formatPriceImpact(quote.priceImpactPct)}
						</span>
					</div>
					<div className="route-row">
						<span className="label">Min received</span>
						<span className="value">
							{minReceived !== null ? fmtNumber(minReceived, 6) : "—"} {toToken?.symbol}
						</span>
					</div>
					<div className="route-row">
						<span className="label">Network fee</span>
						<GaslessFeeDisplay feeSol={feeSol} isLoading={isFeeLoading} />
					</div>
					<div className="route-row">
						<span className="label">Route</span>
						<span className="value" style={{ display: "flex", alignItems: "center", gap: 6 }}>
							{quote.routePlan.map((r, i) => (
								<span key={i} className="route-hop">
									{r.swapInfo.label}
								</span>
							))}
							<button
								className="icon-btn"
								onClick={onRefreshQuote}
								aria-label="Refresh quote"
								style={{ width: 22, height: 22 }}
							>
								<RefreshIcon />
							</button>
						</span>
					</div>
				</div>
			)}

			<button className="btn-primary" disabled={isDisabled} onClick={onSubmit}>
				{btnLabel()}
			</button>
		</div>
	);
}
